import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import { Button, LoadingView } from '@quyva/shared-ui';
import {
    faFolder,
    faSave,
    faShare,
} from '@fortawesome/free-solid-svg-icons';
import { getUrl } from '../pdfDisplay/utils';
import axios from 'axios';
import { Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FileList } from './fileMappingList';
import { getRepoIcon } from '@quyva/common';

const repoMappingData = [
    {
        productCategory: 'productCategory1',
        product: 'product1',
        repoId: 'repoId1',
    },
    {
        productCategory: 'productCategory1',
        product: 'product2',
        repoId: 'repoId2',
    },
];

const fetchProductCategoriesAndProducts = async (user) => {
    try {
        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-user-product-categories-and-products/${user.email}`
        );
        const encodedToken = encodeURIComponent(user.token);
        const response = await axios.get(dynamicUrl, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `${encodedToken}`,
            },
        });

        const data = await response.data.userProductCategoriesAndProducts;
        if (data) {
            return data;
        }
        return [];
    } catch (error) {
        throw new Error(error);
    }
};

const fetchFileProductMappings = async (user, repoId) => {
    try {
        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-file-product-mappings/${repoId}`
        );
        const response = await axios.get(dynamicUrl);

        const data = await response.data.repoFileProductMappings;
        if (data) {
            return data;
        }
        return [];
    } catch (error) {
        throw new Error(error);
    }
};

const saveFileProductMappings = async (
    user,
    setIsLoading,
    setLoadingText,
    repoId,
    fileProductMappings,
    productCategoriesAndProducts,
    getRepoProductMappings
) => {
    try {
        setIsLoading(true);
        const encodedToken = encodeURIComponent(user.token);

        setLoadingText(`Updating Product Category And Products ...`);
        const productsUpdateDynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/update-product-categories-and-products`
        );

        const productsUpdateResponse = await axios.post(
            productsUpdateDynamicUrl,
            {
                user_id: user.email,
                userProductCategoriesAndProducts: productCategoriesAndProducts,
            },

            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${encodedToken}`,
                },
            }
        );

        setLoadingText(`Saving Product Mappings ...`);
        const fileProductMappingData = Object.values(fileProductMappings);
        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/update-file-product-mappings`
        );
        const saveMappingResponse = await axios.post(
            dynamicUrl,
            {
                repo_id: repoId,
                mappings: fileProductMappingData,
                token: user.token,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        await getRepoProductMappings();

        setIsLoading(false);
        setLoadingText('');
    } catch (error) {
        setIsLoading(false);
        setLoadingText('');
        alert(error.message);
    }
};

const RepoMappingList = ({
    selectedRepo,
    productCategoriesAndProducts,
    fileProductMappings,
    setProductCategoriesAndProducts,
    setFileProductMappings,
    allReposFilesData,
    productRepoMappings,
    isSmallScreen,
}) => {
    const parentRepoId =
        selectedRepo?.parent_repo ??
        selectedRepo?.parent_repo_id ??
        selectedRepo?.repoid;

    let mappedFiles = 0;
    let totalFiles = 0;

    Object.keys(fileProductMappings).forEach((fileId) => {
        if (fileProductMappings[fileId].products.length) {
            mappedFiles++;
        }
    });

    if (allReposFilesData && parentRepoId && allReposFilesData.get(parentRepoId)) {
        totalFiles = allReposFilesData.get(parentRepoId).filesData
            .length;
    }

    return (
        <div className='w-full flex flex-col'>
            {totalFiles !== 0 && (
                <div className='w-full flex items-center justify-center p-2 border-b'>
                    <div className='text-texts-secondary300 mx-1'>
                        {mappedFiles === 0 ? (
                            <div className='font-semibold'>No File Mapped</div>
                        ) : (
                            <>
                                <span className='font-bold mr-1.5'>
                                    {mappedFiles}/{totalFiles}
                                </span>
                                {mappedFiles > 1 ? 'Files' : 'File'} Mapped With
                                Product(s)
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className='w-full flex items-center justify-between'>
                <FileList
                    repoId={parentRepoId}
                    productCategoriesAndProducts={productCategoriesAndProducts}
                    setProductCategoriesAndProducts={
                        setProductCategoriesAndProducts
                    }
                    fileProductMappings={fileProductMappings}
                    setFileProductMappings={setFileProductMappings}
                    allReposFilesData={allReposFilesData}
                    productRepoMappings={productRepoMappings}
                    isSmallScreen={isSmallScreen}
                />
            </div>
        </div>
    );
};

// Main Component
export const RepoProductMappings = ({
    user,
    selectedRepo,
    sharedRepos,
    getRepoFilesData,
    allReposFilesData,
    productRepoMappings,
    getRepoProductMappings,
    isSmallScreen,
    onDirtyChange,
}) => {
    const mappingRepoId = selectedRepo?.repoid;
    const isVirtualRepo = !!selectedRepo?.parent_repo;
    const parentRepoId =
        selectedRepo?.parent_repo ??
        selectedRepo?.parent_repo_id ??
        mappingRepoId;

    useEffect(() => {
        setDirty(false);
    }, [mappingRepoId]);

    const [fileProductMappings, setFileProductMappings] = useState({});
    const [productCategoriesAndProducts, setProductCategoriesAndProducts] =
        useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');
    const [isShared, setIsShared] = useState(false);

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasUnsavedMappingEdits, setHasUnsavedMappingEdits] = useState(false);

    const setDirty = (dirty) => {
        const next = !!dirty;
        setHasUnsavedChanges(next);
        onDirtyChange?.(next);
    };

    useEffect(() => {
        if (
            user &&
            user.email &&
            user.token &&
            selectedRepo &&
            selectedRepo.repoid
        ) {
            const init = async (user, repoId) => {
                try {
                    setIsLoading(true);

                    // Product Category and Products
                    setLoadingText(
                        'Loading Product Categories And Products ...'
                    );
                    const categoryAndProductData =
                        await fetchProductCategoriesAndProducts(user);
                    setProductCategoriesAndProducts(categoryAndProductData);

                    // Files data in repos
                    setLoadingText('Loading Files Data ...');
                    // For virtual repos, show parent's files. Fetch only —
                    // don't mark the repo as expanded in the main sidebar.
                    await getRepoFilesData(parentRepoId, false, false);

                    // File and product mappings
                    setLoadingText(`Fetching Product Mappings ...`);
                    const mappingData = await fetchFileProductMappings(
                        user,
                        repoId
                    );
                    const mappings = {};
                    mappingData.forEach((mappingData) => {
                        mappings[mappingData.documentId] = { ...mappingData };
                    });
                    setFileProductMappings(mappings);

                    setIsLoading(false);
                    setLoadingText('');
                } catch (error) {
                    setIsLoading(false);
                    setLoadingText('');
                    alert(error.message);
                }
            };

            for (let index = 0; index < sharedRepos.length; index++) {
                const repo = sharedRepos[index];
                if (repo.repoid === mappingRepoId) {
                    setIsShared(true);
                    break;
                }
            }

            // repoId here is the repo_id used for file_product_mapping (Option A)
            init(user, mappingRepoId);
        }

        return () => {};
    }, [mappingRepoId, parentRepoId, selectedRepo, sharedRepos]);

    if (isLoading) {
        return (
            <div className='w-full h-full flex justify-center items-center'>
                <LoadingView loadingText={loadingText} inLine={false} />
            </div>
        );
    }

    let isSaveDisabled = false;
    const mappedFiles = Object.keys(fileProductMappings);
    for (let index = 0; index < mappedFiles.length; index++) {
        const mappingData = fileProductMappings[mappedFiles[index]];
        if (mappingData && mappingData.products.length === 0) {
            isSaveDisabled = true;
            break;
        }
    }

    return (
        <div className='w-full h-full flex flex-col justify-center'>
            {/* Header */}
            <div className='flex w-full shrink-0 justify-between items-center py-2 px-3 shadow bg-backgrounds-primary100'>
                <div className='w-1/2 flex justify-start items-center font-semibold text-texts-secondary300'>
                    <FontAwesomeIcon
                        icon={getRepoIcon(selectedRepo)}
                        className='text-texts-tertiary200 mr-2'
                    />
                    {selectedRepo.name}
                    {isShared && (
                        <div className='font-semibold ml-4 flex items-center'>
                            <Tag className='text-texts-tertiary400 bg-backgrounds-secondary'>
                                <FontAwesomeIcon
                                    icon={getRepoIcon(selectedRepo)}
                                    className='mr-1'
                                />
                                Shared
                            </Tag>
                        </div>
                    )}
                </div>

                <div className='w-1/2 flex justify-end items-center'>
                    <Button
                        text={isSmallScreen ? null : 'Save'}
                        className='ml-2 text-icons-primary'
                        onClick={async (event) => {
                            try {
                                if (selectedRepo && selectedRepo.repoid) {
                                    if (!isVirtualRepo && hasUnsavedMappingEdits) {
                                        await saveFileProductMappings(
                                            user,
                                            setIsLoading,
                                            setLoadingText,
                                            mappingRepoId,
                                            fileProductMappings,
                                            productCategoriesAndProducts,
                                            getRepoProductMappings
                                        );
                                    }
                                }

                                setDirty(false);
                                setHasUnsavedMappingEdits(false);
                            } finally {
                                setIsLoading(false);
                                setLoadingText('');
                                event.stopPropagation();
                            }
                        }}
                        hoverText={
                            isSaveDisabled
                                ? 'A file is mapped with Product Category but not Products'
                                : 'Save all the mapping changes'
                        }
                        icon={faSave}
                        type={'success'}
                        disabled={!isVirtualRepo ? isSaveDisabled : false}
                    />
                </div>
            </div>

            {/* Mapping List */}
            <div className='w-full flex-1 min-h-0 overflow-auto'>
                {isVirtualRepo ? null : (
                    <RepoMappingList
                        selectedRepo={selectedRepo}
                        productCategoriesAndProducts={productCategoriesAndProducts}
                        setProductCategoriesAndProducts={(next) => {
                            setProductCategoriesAndProducts(next);
                            setHasUnsavedMappingEdits(true);
                            setDirty(true);
                        }}
                        fileProductMappings={fileProductMappings}
                        setFileProductMappings={(next) => {
                            setFileProductMappings(next);
                            setHasUnsavedMappingEdits(true);
                            setDirty(true);
                        }}
                        allReposFilesData={allReposFilesData}
                        productRepoMappings={productRepoMappings}
                        isSmallScreen={isSmallScreen}
                    />
                )}
            </div>
        </div>
    );
};
